import { useCallback, useMemo, useState } from "react";
import { useSharedTable, type ControlSharedTabla } from "shared/table";
import {
    TAMANO_PAGINA_PRODUCTOS,
    TAMANO_PAGINA_SIN_PAGINADOR,
} from "../constants/productoSeleccionado.constants";
import type {
    DetalleProducto,
    FilaProductoSeleccionado,
    OpcionesColumnasProductoSeleccionado,
} from "../types/productoSeleccionado.types";
import {
    calcularTotalPedido,
    crearFilaTotal,
    faltaComentario,
} from "../utils/productoSeleccionado.utils";
import { useColumnasProductoSeleccionado } from "./useColumnasProductoSeleccionado";

interface OpcionesTablaProductoSeleccionado
    extends OpcionesColumnasProductoSeleccionado {
    detalles: DetalleProducto[];
    totalPedido?: number;
    ocultarTotal?: boolean;
    paginador?: boolean;
    tamanoPagina?: number;
}

interface ResultadoTablaProductoSeleccionado {
    control: ControlSharedTabla<FilaProductoSeleccionado>;
    totalPedido: number;
    comentarioAbierto: (fila: FilaProductoSeleccionado) => boolean;
    abiertoManualmente: (idLinea: string) => boolean;
    cerrarComentario: (idLinea: string) => void;
}

export const useProductoSeleccionadoTabla = ({
    detalles,
    totalPedido,
    ocultarTotal = false,
    handleRemoveDetalle,
    onCambiarCantidadLinea,
    onCambiarObservacionesLinea,
    deshabilitado,
    permitirOrdenar,
    paginador = true,
    tamanoPagina = TAMANO_PAGINA_PRODUCTOS,
}: OpcionesTablaProductoSeleccionado): ResultadoTablaProductoSeleccionado => {
    /* SUB-FILA DE COMENTARIO */
    const [abiertos, setAbiertos] = useState<Set<string>>(() => new Set());
    const editableComentario = Boolean(onCambiarObservacionesLinea);

    const onAlternarComentario = useCallback((idLinea: string) => {
        setAbiertos((prev) => {
            const siguiente = new Set(prev);
            if (siguiente.has(idLinea)) siguiente.delete(idLinea);
            else siguiente.add(idLinea);
            return siguiente;
        });
    }, []);

    const cerrarComentario = useCallback((idLinea: string) => {
        setAbiertos((prev) => {
            if (!prev.has(idLinea)) return prev;
            const siguiente = new Set(prev);
            siguiente.delete(idLinea);
            return siguiente;
        });
    }, []);

    const abiertoManualmente = useCallback(
        (idLinea: string) => abiertos.has(idLinea),
        [abiertos]
    );

    const comentarioAbierto = useCallback(
        (fila: FilaProductoSeleccionado) =>
            editableComentario &&
            !fila.esTotal &&
            (abiertos.has(fila.id_linea) || faltaComentario(fila)),
        [editableComentario, abiertos]
    );

    /* COLUMNAS */
    const columnas = useColumnasProductoSeleccionado({
        handleRemoveDetalle,
        onCambiarCantidadLinea,
        onCambiarObservacionesLinea,
        deshabilitado,
        permitirOrdenar,
        comentarioAbierto,
        onAlternarComentario,
    });

    /* TOTAL */
    const total = useMemo(
        () => totalPedido ?? calcularTotalPedido(detalles),
        [totalPedido, detalles]
    );

    const filasPie = useMemo<FilaProductoSeleccionado[]>(
        () => (ocultarTotal ? [] : [crearFilaTotal(total)]),
        [ocultarTotal, total]
    );

    const control = useSharedTable<FilaProductoSeleccionado>({
        columnas,
        datos: detalles,
        filasPie,
        tamanoPaginaInicial: paginador ? tamanoPagina : TAMANO_PAGINA_SIN_PAGINADOR,
    });

    return { control, totalPedido: total, comentarioAbierto, abiertoManualmente, cerrarComentario };
};